// Aba "Lista de UCs" da Planilha Gran: quais UCs entram na planilha e em qual condomínio.
// Começa com as 26 UCs do modelo de 07/2026; as extras (UCs da Gran fora do modelo) são
// incluídas aqui pela Meyryene. Cadastro de UC (vínculo com a Gran, situação) NÃO se
// corrige aqui — é com outra pessoa; a lista só aponta o problema.
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check, FileSpreadsheet, Loader2, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import ConfirmationModal from '../../../src/components/ConfirmationModal';
import { useToast } from '../../../src/contexts/ToastContext';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logAction } from '../../../src/lib/logger';
import type { ItemPlanilhaGran, UcPlanilhaGran } from './planilhaGranModel';
import * as api from './planilhaGranApi';
import LerPlanilhaGranModal from './LerPlanilhaGranModal';

interface Props {
  empresaId: string;
  itens: ItemPlanilhaGran[];
  ucs: UcPlanilhaGran[];
  podeGerir: boolean;
  onMudou: () => void;
}

const DESTINO_NOME_CLIENTE = '__nome_cliente__';
const DESTINO_NOVO = '__novo__';
const semAcento = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const digitos = (s: unknown) => String(s ?? '').replace(/\D/g, '').replace(/^0+/, '');

const AbaListaGran: React.FC<Props> = ({ empresaId, itens, ucs, podeGerir, onMudou }) => {
  const { addToast } = useToast();
  const { user } = useAuth();
  const [busca, setBusca] = useState('');
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [destino, setDestino] = useState(DESTINO_NOME_CLIENTE);
  const [novoGrupo, setNovoGrupo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [remover, setRemover] = useState<ItemPlanilhaGran | null>(null);
  const [renomeando, setRenomeando] = useState<{ grupo: string; nome: string } | null>(null);
  const [lendoPlanilha, setLendoPlanilha] = useState(false);
  const [tirarForaDoSistema, setTirarForaDoSistema] = useState(false);

  const ucPorId = useMemo(() => new Map(ucs.map(u => [Number(u.id), u])), [ucs]);
  const grupos = useMemo(() => {
    const m = new Map<string, ItemPlanilhaGran[]>();
    itens.forEach(i => { const g = m.get(i.grupo); if (g) g.push(i); else m.set(i.grupo, [i]); });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0], 'pt-BR'));
  }, [itens]);
  const nomesGrupos = grupos.map(([g]) => g);

  // Candidatas a extra: UCs da Gran no cadastro que ainda não estão na lista.
  const naLista = useMemo(() => new Set(itens.map(i => Number(i.unidade_consumidora_id))), [itens]);
  const candidatas = useMemo(() => {
    const termo = semAcento(busca.trim());
    const termoUc = digitos(busca);
    return ucs
      .filter(u => u.empresa_parceira_id === empresaId && !naLista.has(Number(u.id)))
      .filter(u => !termo
        || semAcento(u.nome_cliente || '').includes(termo)
        || (termoUc.length >= 3 && digitos(u.numero_uc).includes(termoUc)))
      .sort((a, b) => (a.nome_cliente || '').localeCompare(b.nome_cliente || '', 'pt-BR'));
  }, [ucs, empresaId, naLista, busca]);

  const incluir = async () => {
    const ids = Array.from(marcadas);
    if (!ids.length) return;
    const grupo = destino === DESTINO_NOME_CLIENTE ? null : destino === DESTINO_NOVO ? novoGrupo.trim() : destino;
    if (destino === DESTINO_NOVO && !grupo) { addToast('Digite o nome do novo condomínio.', 'warning'); return; }
    setSalvando(true);
    try {
      const n = await api.incluirUcs(empresaId, ids, grupo);
      await logAction(user?.id || 'SYSTEM', 'Incluiu UCs na planilha Gran', { uc_ids: ids, grupo: grupo ?? 'nome do cliente', incluidas: n });
      addToast(n === ids.length ? `${n} UC${n !== 1 ? 's' : ''} incluída${n !== 1 ? 's' : ''} na planilha.` : `${n} de ${ids.length} incluídas — as outras já estavam na lista.`, 'success');
      setMarcadas(new Set());
      setNovoGrupo('');
      onMudou();
    } catch (e: any) {
      addToast(e.message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const confirmarRemocao = async () => {
    if (!remover) return;
    setSalvando(true);
    try {
      await api.removerItens([remover.id]);
      await logAction(user?.id || 'SYSTEM', 'Tirou UC da planilha Gran', { item_id: remover.id, numero_uc: remover.numero_uc, grupo: remover.grupo });
      addToast(`UC ${remover.numero_uc} saiu da planilha.`, 'success');
      setRemover(null);
      onMudou();
    } catch (e: any) {
      addToast(e.message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const mover = async (item: ItemPlanilhaGran, grupo: string) => {
    if (!grupo || grupo === item.grupo) return;
    setSalvando(true);
    try {
      await api.moverGrupo([item.id], grupo);
      await logAction(user?.id || 'SYSTEM', 'Mudou UC de condomínio na planilha Gran', { item_id: item.id, de: item.grupo, para: grupo });
      onMudou();
    } catch (e: any) {
      addToast(e.message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  const salvarNomeGrupo = async () => {
    if (!renomeando) return;
    const nome = renomeando.nome.trim();
    if (!nome || nome.toUpperCase() === renomeando.grupo) { setRenomeando(null); return; }
    const ids = itens.filter(i => i.grupo === renomeando.grupo).map(i => i.id);
    setSalvando(true);
    try {
      await api.moverGrupo(ids, nome);
      await logAction(user?.id || 'SYSTEM', 'Renomeou condomínio na planilha Gran', { de: renomeando.grupo, para: nome.toUpperCase(), itens: ids.length });
      setRenomeando(null);
      onMudou();
    } catch (e: any) {
      addToast(e.message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  // Esc fecha a Planilha Gran inteira (ver PlanilhaGranModal) — enquanto o
  // ConfirmationModal de "tirar da planilha" está aberto, Esc deve fechar só ele.
  // Intercepta na fase de captura, antes do listener global da planilha.
  useEffect(() => {
    if (!remover && !tirarForaDoSistema) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setRemover(null); setTirarForaDoSistema(false); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [remover, tirarForaDoSistema]);

  const avisoDaUc = (item: ItemPlanilhaGran) => {
    const uc = ucPorId.get(Number(item.unidade_consumidora_id));
    if (!uc) return 'UC não encontrada no cadastro';
    if (uc.empresa_parceira_id !== empresaId) return 'Não vinculada à Gran no cadastro';
    if ((uc.situacao || '').trim() === 'Cancelada') return 'Cancelada no cadastro';
    return '';
  };

  // UCs que saíram do sistema (cancelada, fora da Gran, sem cadastro). Não saem sozinhas da
  // planilha — decisão do Victor (#6116): a tela avisa e quem usa tira.
  const foraDoSistema = itens.filter(i => avisoDaUc(i));

  const confirmarTirarForaDoSistema = async () => {
    const ids = foraDoSistema.map(i => i.id);
    if (!ids.length) { setTirarForaDoSistema(false); return; }
    setSalvando(true);
    try {
      const n = await api.removerItens(ids);
      await logAction(user?.id || 'SYSTEM', 'Tirou da planilha Gran as UCs que saíram do sistema', { itens: foraDoSistema.map(i => ({ id: i.id, numero_uc: i.numero_uc, motivo: avisoDaUc(i) })), removidas: n });
      addToast(`${n} UC${n !== 1 ? 's' : ''} saí${n !== 1 ? 'ram' : 'u'} da planilha.`, 'success');
      setTirarForaDoSistema(false);
      onMudou();
    } catch (e: any) {
      addToast(e.message, 'error');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className={`h-full min-h-0 grid gap-3 p-3 ${podeGerir ? 'lg:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]' : ''}`}>
      {/* Lista atual */}
      <section className="min-h-0 flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <header className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
          <h3 className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">Na planilha: {itens.length} UC{itens.length !== 1 ? 's' : ''} em {grupos.length} condomínio{grupos.length !== 1 ? 's' : ''}</h3>
          <div className="flex items-start gap-2">
            <p className="flex-1 text-[11px] text-gray-500 dark:text-gray-400">Tirar uma UC da lista não apaga nada: ela só deixa de sair na planilha.</p>
            {podeGerir && (
              <button type="button" onClick={() => setLendoPlanilha(true)} className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-primary hover:underline" title="Atualizar a lista com o Excel de conferência da Gran">
                <FileSpreadsheet className="w-3.5 h-3.5" aria-hidden="true" /> Ler planilha da Gran
              </button>
            )}
          </div>
        </header>
        {foraDoSistema.length > 0 && (
          <div role="status" className="px-3 py-2 flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100 dark:border-amber-900/40">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden="true" />
            <p className="flex-1 text-[12px] text-amber-900 dark:text-amber-200">
              {foraDoSistema.length} UC{foraDoSistema.length !== 1 ? 's' : ''} da planilha {foraDoSistema.length !== 1 ? 'saíram' : 'saiu'} do sistema (cancelada, fora da Gran ou sem cadastro). Elas continuam na planilha até alguém tirar — veja o aviso em cada uma.
            </p>
            {podeGerir && (
              <button type="button" onClick={() => setTirarForaDoSistema(true)} disabled={salvando} className="shrink-0 text-[12px] font-semibold text-amber-800 dark:text-amber-200 hover:underline">
                Tirar {foraDoSistema.length === 1 ? 'da planilha' : `as ${foraDoSistema.length} da planilha`}
              </button>
            )}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
          {grupos.map(([grupo, its]) => (
            <div key={grupo}>
              <div className="px-3 py-1.5 bg-white dark:bg-gray-900 sticky top-0 flex items-center gap-2 border-b border-gray-50 dark:border-gray-800">
                {renomeando?.grupo === grupo ? (
                  <form className="flex items-center gap-1 flex-1" onSubmit={e => { e.preventDefault(); salvarNomeGrupo(); }}>
                    <input
                      autoFocus
                      value={renomeando.nome}
                      onChange={e => setRenomeando({ grupo, nome: e.target.value })}
                      aria-label={`Novo nome do condomínio ${grupo}`}
                      className="flex-1 text-[12px] font-semibold uppercase bg-white border border-gray-200 rounded-md px-2 py-1 dark:bg-gray-800 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
                    />
                    <button type="submit" disabled={salvando} className="p-1 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded" aria-label="Salvar nome"><Check className="w-4 h-4" /></button>
                    <button type="button" onClick={() => setRenomeando(null)} className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Cancelar"><X className="w-4 h-4" /></button>
                  </form>
                ) : (
                  <>
                    <span className="text-[12px] font-bold text-gray-800 dark:text-gray-100 uppercase flex-1 truncate">{grupo}</span>
                    <span className="text-[11px] text-gray-400">{its.length}</span>
                    {podeGerir && (
                      <button type="button" onClick={() => setRenomeando({ grupo, nome: grupo })} className="p-1 text-gray-400 hover:text-primary rounded" title="Renomear condomínio" aria-label={`Renomear ${grupo}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </>
                )}
              </div>
              <ul>
                {its.map(item => {
                  const uc = ucPorId.get(Number(item.unidade_consumidora_id));
                  const aviso = avisoDaUc(item);
                  return (
                    <li key={item.id} className="px-3 py-1.5 text-[12px]">
                      <div className="flex items-center gap-2">
                      <span className="font-mono text-gray-700 dark:text-gray-200 w-36 shrink-0">{uc?.numero_uc || item.numero_uc}</span>
                      <span className="truncate text-gray-500 dark:text-gray-400 flex-1">{uc?.nome_cliente || '—'}</span>
                      {item.origem === 'extra' && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300">Extra</span>}
                      {aviso && <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 shrink-0">{aviso}</span>}
                      {podeGerir && (
                        <>
                          <select
                            value=""
                            onChange={e => mover(item, e.target.value)}
                            disabled={salvando}
                            aria-label={`Mover UC ${item.numero_uc} para outro condomínio`}
                            className="text-[11px] max-w-[9rem] bg-white border border-gray-200 rounded-md px-1.5 py-1 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-200"
                          >
                            <option value="">Mover para…</option>
                            {nomesGrupos.filter(g => g !== item.grupo).map(g => <option key={g} value={g}>{g}</option>)}
                          </select>
                          <button type="button" onClick={() => setRemover(item)} className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" title="Tirar da planilha" aria-label={`Tirar UC ${item.numero_uc} da planilha`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      </div>
                      {/* Anotação da conferência com a Gran (#6116) — só registro, não gera tarefa. */}
                      {item.anotacao && (
                        <p className="mt-1 ml-[9.5rem] text-[11px] leading-snug text-gray-500 dark:text-gray-400 whitespace-pre-line">
                          <span className="font-semibold text-gray-600 dark:text-gray-300">Anotação da Gran: </span>{item.anotacao}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Incluir extras */}
      {podeGerir && (
        <section className="min-h-0 flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <header className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/60">
            <h3 className="text-[13px] font-semibold text-gray-800 dark:text-gray-100">Incluir UCs extras da Gran</h3>
            <p className="text-[11px] text-gray-500 dark:text-gray-400">{candidatas.length} UC{candidatas.length !== 1 ? 's' : ''} da Gran no cadastro ainda fora da planilha</p>
          </header>
          <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2" aria-hidden="true" />
              <input
                value={busca}
                onChange={e => setBusca(e.target.value)}
                placeholder="Buscar por nome ou UC"
                aria-label="Buscar UC da Gran para incluir"
                className="w-full text-[12px] bg-white border border-gray-200 rounded-lg pl-8 pr-3 py-1.5 dark:bg-gray-800 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
              />
            </div>
          </div>
          <ul className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
            {candidatas.map(u => {
              const id = Number(u.id);
              return (
                <li key={id}>
                  <label className="px-3 py-1.5 flex items-center gap-2 text-[12px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/40">
                    <input
                      type="checkbox"
                      checked={marcadas.has(id)}
                      onChange={e => setMarcadas(prev => { const n = new Set(prev); e.target.checked ? n.add(id) : n.delete(id); return n; })}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/50"
                    />
                    <span className="font-mono text-gray-700 dark:text-gray-200 w-36 shrink-0">{u.numero_uc}</span>
                    <span className="truncate text-gray-600 dark:text-gray-300 flex-1">{u.nome_cliente || '—'}</span>
                    {(u.situacao || '').trim() === 'Cancelada' && <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">Cancelada</span>}
                  </label>
                </li>
              );
            })}
            {candidatas.length === 0 && (
              <li className="px-3 py-6 text-center text-[12px] text-gray-500 dark:text-gray-400">
                {busca ? 'Nenhuma UC da Gran com esse nome ou número fora da planilha.' : 'Todas as UCs da Gran já estão na planilha.'}
              </li>
            )}
          </ul>
          <footer className="px-3 py-2.5 border-t border-gray-100 dark:border-gray-800 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor="destino-grupo-gran" className="text-[11px] font-semibold text-gray-500">Condomínio</label>
              <select
                id="destino-grupo-gran"
                value={destino}
                onChange={e => setDestino(e.target.value)}
                className="flex-1 min-w-[10rem] text-[12px] bg-white border border-gray-200 rounded-lg px-2 py-1.5 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              >
                <option value={DESTINO_NOME_CLIENTE}>Nome do cliente de cada UC</option>
                <option value={DESTINO_NOVO}>Novo condomínio…</option>
                {nomesGrupos.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            {destino === DESTINO_NOVO && (
              <input
                value={novoGrupo}
                onChange={e => setNovoGrupo(e.target.value)}
                placeholder="Nome do novo condomínio"
                aria-label="Nome do novo condomínio"
                className="w-full text-[12px] uppercase bg-white border border-gray-200 rounded-lg px-3 py-1.5 dark:bg-gray-800 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none"
              />
            )}
            <button
              type="button"
              onClick={incluir}
              disabled={salvando || marcadas.size === 0}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-[12px] font-semibold rounded-lg bg-primary hover:bg-primary/90 text-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {salvando ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Plus className="w-4 h-4" aria-hidden="true" />}
              Incluir {marcadas.size || ''} na planilha
            </button>
          </footer>
        </section>
      )}

      {lendoPlanilha && (
        <LerPlanilhaGranModal empresaId={empresaId} itens={itens} ucs={ucs} onClose={() => setLendoPlanilha(false)} onGravou={onMudou} />
      )}

      <ConfirmationModal
        isOpen={tirarForaDoSistema}
        onClose={() => setTirarForaDoSistema(false)}
        onConfirm={confirmarTirarForaDoSistema}
        isLoading={salvando}
        title={`Tirar ${foraDoSistema.length} UC${foraDoSistema.length !== 1 ? 's' : ''} da planilha?`}
        description={`Saem da planilha da Gran: ${foraDoSistema.map(i => `${i.numero_uc} (${avisoDaUc(i).charAt(0).toLowerCase()}${avisoDaUc(i).slice(1)})`).join('; ')}. As faturas e os boletos delas não mudam, e as planilhas já enviadas continuam no histórico.`}
        confirmText="Tirar da planilha"
        variant="warning"
      />

      <ConfirmationModal
        isOpen={!!remover}
        onClose={() => setRemover(null)}
        onConfirm={confirmarRemocao}
        isLoading={salvando}
        title="Tirar esta UC da planilha?"
        description={remover ? `A UC ${remover.numero_uc} (${remover.grupo}) deixa de sair na planilha da Gran a partir de agora. As faturas e os boletos dela não mudam, e as planilhas já enviadas continuam no histórico. Dá para incluir de novo depois.` : ''}
        confirmText="Tirar da planilha"
        variant="warning"
      />
    </div>
  );
};

export default AbaListaGran;
